/**
 * Panel-side ky API client with cookie-based auth (D-06).
 *
 * Uses credentials: 'include' to send httpOnly cookies on every request.
 * On 401, attempts a cookie refresh via POST /v1/auth/refresh (no body
 * needed since refresh token is in the acc_refresh cookie). Deduplicates
 * concurrent refresh attempts via a shared Promise (RESEARCH.md Pitfall 4).
 * The x-acc-retry guard header prevents infinite retry loops.
 */
import ky from 'ky';

/** Module-level refresh dedup (RESEARCH.md Pitfall 4). */
let refreshPromise: Promise<boolean> | null = null;

async function attemptRefresh(): Promise<boolean> {
  try {
    const res = await fetch('/v1/auth/refresh', {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({}),
    });
    return res.ok;
  } catch {
    return false;
  }
}

function refreshOnce(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = attemptRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

export const api = ky.create({
  prefix: '/v1',
  credentials: 'include',
  timeout: 15_000,
  retry: 0,
  hooks: {
    afterResponse: [
      async ({ request, response }) => {
        if (response.status !== 401) return response;

        // Guard: retried request already carries x-acc-retry header.
        if (request.headers.get('x-acc-retry')) return response;

        const refreshed = await refreshOnce();
        if (!refreshed) {
          window.location.href = '/login';
          return response;
        }

        // Clone the original Request and mark with guard header.
        const retried = new Request(request, {
          headers: new Headers(request.headers),
        });
        retried.headers.set('x-acc-retry', '1');
        return fetch(retried);
      },
    ],
  },
});
